import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

// Home News Section
const HomeNewsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const latestNews = {
    headline: "Feeding the soul: Creating a lifeline for families in hard times",
    source: "CBS Sunday Morning",
    image: "https://i.ytimg.com/vi/mAt2sXurd5Q/maxresdefault.jpg",
    link: "https://www.youtube.com/watch?v=mAt2sXurd5Q"
  };

  return (
    <section 
      ref={ref}
      data-testid="home-news-section"
      className="py-12 md:py-16 px-4 md:px-8 lg:px-12 bg-white"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 
            data-testid="home-news-header"
            className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            News
          </h2>
          <div className="w-24 h-1 bg-[#C41E3A] mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Latest News Story */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-[#C41E3A] rounded-xl shadow-sm overflow-hidden"
          >
            <a 
              href={latestNews.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block aspect-video w-full overflow-hidden"
            >
              <img 
                src={latestNews.image} 
                alt={latestNews.headline}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </a>
            <div className="p-6">
              <p className="text-white/80 text-sm font-medium mb-2">{latestNews.source}</p>
              <h3 
                className="font-bold text-white text-lg mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {latestNews.headline}
              </h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <a 
                  href={latestNews.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-news-watch"
                  className="text-white hover:text-white/80 font-medium inline-flex items-center gap-1 transition-colors"
                >
                  Watch video
                  <ChevronRight size={16} />
                </a>
                <Link 
                  to="/news/in-the-news"
                  data-testid="home-news-more"
                  className="text-white/80 hover:text-white font-medium inline-flex items-center gap-1 transition-colors"
                >
                  More news stories
                  <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Facebook Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-[#C41E3A] rounded-xl shadow-sm overflow-hidden"
          >
            <div className="p-6 h-full flex flex-col">
              {/* Facebook Page Plugin */}
              <div className="flex-1 min-h-[300px] bg-white rounded-lg overflow-hidden">
                <iframe
                  src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2Fmurphysgivingmarket&tabs=timeline&width=500&height=400&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=false"
                  width="100%"
                  height="100%"
                  style={{ border: 'none', overflow: 'hidden', minHeight: '300px' }}
                  scrolling="no"
                  frameBorder="0"
                  allowFullScreen={true}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Murphy's Giving Market Facebook"
                ></iframe>
              </div>
              
              <a 
                href="https://www.facebook.com/murphysgivingmarket"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-facebook-link"
                className="mt-4 text-white hover:text-white/80 font-medium inline-flex items-center gap-2 transition-colors"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                >
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Follow Us on Facebook
                <ChevronRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HomeNewsSection;
